import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "https://api.estatecoin.in";

function fmt(n) {
  if (n >= 10000000) return "₹" + (n / 10000000).toFixed(2) + " Cr";
  if (n >= 100000)   return "₹" + (n / 100000).toFixed(1) + "L";
  if (n >= 1000)     return "₹" + (n / 1000).toFixed(0) + "K";
  return "₹" + n;
}

const PARKING_OPTIONS = [
  { id: '6911e2d435152a1bbfec7e31', name: 'Pre-leased Commercial Hub', yield: 7, tokenPrice: 1500 },
  { id: '6911e2d435152a1bbfec7e32', name: 'Pre-leased Residential', yield: 5.5, tokenPrice: 500 },
  { id: '6911e2d435152a1bbfec7e33', name: 'Under-construction Commercial', yield: 0, tokenPrice: 1000 },
  { id: '6911e2d435152a1bbfec7e34', name: 'Under-construction Residential', yield: 0, tokenPrice: 720 },
];

const FALLBACK = [{
  id: "trilive-andheri-2026",
  name: "Studio Apartment Near Mumbai CSMT Airport",
  developer: "Premium Developer",
  location: "Marol, Andheri East, Mumbai",
  type: "Managed Co-living Studio",
  targetAmount: 7000000,
  raisedAmount: 0,
  rentalYield: 6,
  escalation: "15% every 3 years",
  leaseYears: 10,
  managedBy: "Professional Management",
  minCommit: 100,
  status: "pooling",
  parkingIn: "Pre-leased Commercial Hub",
  parkingYield: 5.5,
  expectedClose: "Q3 2026",
  highlights: [
    "6% assured rental p.a. from Housr",
    "15% escalation every 3 years",
    "10-year lease agreement",
    "Professionally managed — zero landlord hassle",
    "Andheri East — Mumbai's corporate hub",
  ],
}];

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');

  .ec-comm * { box-sizing: border-box; }

  /* ── HOW IT WORKS TABS ── */
  .ec-how-tab { transition: all 0.2s; }
  .ec-how-tab:hover { transform: translateY(-2px); }

  /* ── PROPERTY CARD ── */
  .ec-prop-card { transition: box-shadow 0.3s; }
  .ec-prop-card:hover { box-shadow: 0 20px 60px rgba(0,0,0,0.12); }

  /* ── COMMIT BUTTON ── */
  .ec-commit-btn {
    position: relative; overflow: hidden;
    transition: all 0.3s;
  }
  .ec-commit-btn::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(135deg, transparent, rgba(255,255,255,0.15), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s;
  }
  .ec-commit-btn:hover::after { transform: translateX(100%); }
  .ec-commit-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(27,58,45,0.4); }

  /* ── QUICK AMOUNT BTN ── */
  .ec-qa:hover { border-color: #1B3A2D !important; color: #1B3A2D !important; }

  /* ── HIGHLIGHT CHIP ── */
  .ec-chip { transition: all 0.15s; }
  .ec-chip:hover { border-color: #C9A84C !important; background: #FDF9F0 !important; }

  /* ── STAT CARD ── */
  .ec-stat { transition: all 0.2s; }
  .ec-stat:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.08); }

  /* ── PULSE ── */
  @keyframes ec-pulse {
    0%,100% { box-shadow: 0 0 0 3px rgba(34,197,94,0.3); }
    50%      { box-shadow: 0 0 0 7px rgba(34,197,94,0.08); }
  }
  .ec-live-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: #22c55e;
    animation: ec-pulse 1.8s infinite;
  }

  /* ── PROGRESS BAR ── */
  @keyframes ec-fill { from { width: 0% } }
  .ec-fill { animation: ec-fill 1.2s cubic-bezier(0.4,0,0.2,1) forwards; }

  /* ── FADE UP ── */
  @keyframes ec-fadeup { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  .ec-fadeup { animation: ec-fadeup 0.5s ease forwards; }
`;

export default function CommunityProperty({ user, compact = false }) {
  const [properties, setProperties] = useState(FALLBACK);
  const [commitAmount, setCommitAmount] = useState(1000);
  const [submitted, setSubmitted] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeHow, setActiveHow] = useState(0);
  const [parkingPropertyId, setParkingPropertyId] = useState('6911e2d435152a1bbfec7e31');
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/api/community-properties`, { credentials: "include" })
      .then(r => r.json())
      .then(d => { if (d?.properties?.length) setProperties(d.properties); })
      .catch(() => {});
  }, []);

  const handleCommit = async (propId, propName, parkingIn) => {
    if (!user) { navigate("/register"); return; }
    if (commitAmount < 100) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/community-properties/commit`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ propertyId: propId, amount: commitAmount, parkInPropertyId: parkingPropertyId }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        setSubmitted(s => ({ ...s, [propId]: { msg: d.message, tokens: d.tokens, parking: d.parkingProperty } }));
      } else {
        alert(d.error || 'Something went wrong. Please try again.');
      }
    } catch (e) { alert('Network error. Please try again.'); }
    setLoading(false);
  };

  const HOW_STEPS = [
    { icon: "🔍", title: "We shortlist", body: "Our team does full due diligence on a premium property — yield analysis, developer background, lease terms." },
    { icon: "💰", title: "You commit", body: "Commit any amount from ₹100. Your money is immediately invested in our Pre-leased Commercial Hub (~7% p.a.) — zero idle time from day 1." },
    { icon: "📊", title: "Pool fills up", body: "The progress bar fills as more investors join. You get notified at 50%, 75%, and 100% milestones. No pressure — withdraw anytime." },
    { icon: "🏢", title: "We acquire", body: "Target reached → White Soil Advisors LLP (LLPIN: AAT-7542) acquires the property on behalf of all investors. Legally documented." },
    { icon: "📜", title: "You own it", body: "Digital ownership certificate issued. Rental income from the new property starts flowing to your wallet. Your investment upgraded automatically." },
  ];

  return (
    <div className="ec-comm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{STYLES}</style>

      {/* ── SECTION INTRO ── */}
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#F0F7F3", border: "1px solid #C8E6D4", borderRadius: 100, padding: "6px 16px", marginBottom: 16 }}>
          <div className="ec-live-dot" />
          <span style={{ fontSize: 11, fontWeight: 700, color: "#1B3A2D", letterSpacing: "0.1em", textTransform: "uppercase" }}>Community Properties — Live Now</span>
        </div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(24px,4vw,38px)", fontWeight: 700, color: "#111", margin: "0 0 12px", lineHeight: 1.2 }}>
          Pool together.<br />Own premium real estate.
        </h2>
        <p style={{ fontSize: 15, color: "#666", maxWidth: 520, margin: "0 auto", lineHeight: 1.7 }}>
          Too expensive to buy alone? We shortlist premium properties, you pool funds with other investors, White Soil Advisors LLP acquires it. You get fractional ownership — and rental income from day 1.
        </p>
      </div>

      {/* ── HOW IT WORKS — TABBED ── */}
      <div style={{ background: "#1B3A2D", borderRadius: 20, padding: "28px 28px 24px", marginBottom: 32 }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 16 }}>How it works</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
          {HOW_STEPS.map((s, i) => (
            <button key={i} className="ec-how-tab" onClick={() => setActiveHow(i)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: "none", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 12,
                background: activeHow === i ? "rgba(201,168,76,0.2)" : "rgba(255,255,255,0.05)",
                color: activeHow === i ? "#C9A84C" : "rgba(255,255,255,0.5)",
                borderBottom: activeHow === i ? "2px solid #C9A84C" : "2px solid transparent",
              }}>
              <span>{s.icon}</span> {s.title}
            </button>
          ))}
        </div>
        <div className="ec-fadeup" key={activeHow} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(201,168,76,0.15)", border: "1px solid rgba(201,168,76,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
            {HOW_STEPS[activeHow].icon}
          </div>
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 600, color: "#fff", marginBottom: 6 }}>{HOW_STEPS[activeHow].title}</div>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.65)", lineHeight: 1.7 }}>{HOW_STEPS[activeHow].body}</div>
          </div>
        </div>
        {/* Key guarantee strip */}
        <div style={{ display: "flex", gap: 12, marginTop: 20, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.08)", flexWrap: "wrap" }}>
          {[
            { icon: "💡", text: "Money earns from Day 1" },
            { icon: "🔒", text: "Legally acquired by White Soil Advisors LLP" },
            { icon: "↩️", text: "Withdraw anytime if target not reached" },
          ].map((g, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
              <span>{g.icon}</span> {g.text}
            </div>
          ))}
        </div>
      </div>

      {/* ── PROPERTY CARDS ── */}
      {properties.map(p => {
        const raised = p.raisedAmount || 0;
        const pct = Math.min((raised / p.targetAmount) * 100, 100);
        const isSubmitted = submitted[p.id];
        const submitData = typeof isSubmitted === 'object' ? isSubmitted : null;

        return (
          <div key={p.id} className="ec-prop-card" style={{ background: "#fff", borderRadius: 24, overflow: "hidden", marginBottom: 24, border: "1px solid #EDE4DC", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

            {/* ── HERO IMAGE AREA ── */}
            <div style={{ position: "relative", height: 260, background: "linear-gradient(135deg, #1B3A2D 0%, #2D5C45 50%, #1B3A2D 100%)", overflow: "hidden" }}>
              <img
                src={p.imagePath || "/assets/properties/trilive_andheri.webp"}
                alt={p.name}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.7 }}
                onError={e => { e.target.style.display = "none"; }}
              />
              {/* SVG building facade */}
              <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.25 }} viewBox="0 0 900 260" preserveAspectRatio="xMidYMid slice">
                {Array.from({ length: 8 }).map((_, col) =>
                  Array.from({ length: 5 }).map((_, row) => (
                    <rect key={`${col}-${row}`}
                      x={60 + col * 105} y={20 + row * 46}
                      width={70} height={32} rx={3}
                      fill={Math.random() > 0.5 ? "rgba(255,220,100,0.4)" : "rgba(255,255,255,0.08)"}
                      stroke="rgba(255,255,255,0.1)" strokeWidth={0.5}
                    />
                  ))
                )}
                <line x1="450" y1="0" x2="450" y2="260" stroke="rgba(255,255,255,0.1)" strokeWidth={3} />
              </svg>
              {/* Gradient overlay */}
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)" }} />
              {/* Gold accent line */}
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg, #C9A84C, #E8C97A, #C9A84C)" }} />

              {/* Badges top */}
              <div style={{ position: "absolute", top: 16, left: 20, right: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(34,197,94,0.15)", backdropFilter: "blur(8px)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 100, padding: "5px 12px" }}>
                  <div className="ec-live-dot" />
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#4ade80", letterSpacing: "0.1em" }}>LIVE — POOLING NOW</span>
                </div>
              </div>

              {/* Yield badge — floating right */}
              <div style={{ position: "absolute", top: 52, right: 20, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(12px)", border: "1px solid rgba(201,168,76,0.4)", borderRadius: 14, padding: "12px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Rental yield</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700, color: "#C9A84C", lineHeight: 1 }}>{p.rentalYield}%</div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>p.a. assured</div>
                <div style={{ fontSize: 9, color: "#C9A84C", marginTop: 4 }}>📈 {p.escalation}</div>
              </div>

              {/* Title area */}
              <div style={{ position: "absolute", bottom: 20, left: 20, right: 160 }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>
                  {p.developer} · {p.type}
                </div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(18px,3vw,26px)", fontWeight: 700, color: "#fff", lineHeight: 1.2, marginBottom: 6 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", display: "flex", alignItems: "center", gap: 6 }}>
                  📍 {p.location} &nbsp;·&nbsp; Managed by <strong style={{ color: "rgba(255,255,255,0.85)" }}>{p.managedBy}</strong>
                </div>
              </div>
            </div>

            {/* ── STATS ROW ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", borderBottom: "1px solid #F0EDE8" }}>
              {[
                { label: "Target", value: fmt(p.targetAmount) },
                { label: "Lease", value: `${p.leaseYears} years` },
                { label: "Closes", value: p.expectedClose },
                { label: "Min. commit", value: `₹${p.minCommit}` },
              ].map((s, i) => (
                <div key={i} className="ec-stat" style={{ padding: "14px 16px", textAlign: "center", borderRight: i < 3 ? "1px solid #F0EDE8" : "none", background: "#FAFAF8", cursor: "default" }}>
                  <div style={{ fontSize: 11, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1B3A2D" }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* ── BODY ── */}
            <div style={{ padding: "24px" }}>

              {/* Progress */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                  <div>
                    <span style={{ fontSize: 20, fontWeight: 700, color: "#1B3A2D" }}>{fmt(raised)}</span>
                    <span style={{ fontSize: 13, color: "#999", marginLeft: 6 }}>raised of {fmt(p.targetAmount)}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: pct > 0 ? "#1B3A2D" : "#ccc" }}>{pct.toFixed(1)}%</span>
                </div>
                <div style={{ height: 10, background: "#F0EDE8", borderRadius: 999, overflow: "hidden" }}>
                  <div className="ec-fill" style={{ height: "100%", width: `${Math.max(pct, 0.3)}%`, background: "linear-gradient(90deg, #1B3A2D, #2D7A50)", borderRadius: 999 }} />
                </div>
                <div style={{ fontSize: 11, color: "#aaa", marginTop: 5 }}>Be among the first investors · {p.investorCount || 0} committed so far</div>
              </div>

              {/* Two-column: highlights + parking notice */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
                {/* Highlights */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Property highlights</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {(p.highlights || []).map((h, i) => (
                      <div key={i} className="ec-chip" style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 12px", background: "#F8F5F0", border: "1px solid #EDE4DC", borderRadius: 10, fontSize: 12, color: "#444", cursor: "default" }}>
                        <span style={{ color: "#1B3A2D", fontWeight: 700, flexShrink: 0 }}>✓</span> {h}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Parking + developer info */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {/* Auto-park notice */}
                  <div style={{ background: "linear-gradient(135deg, #F0F7F3, #E8F4EE)", border: "1px solid #C8E6D4", borderRadius: 14, padding: "16px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#1B3A2D", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>💡 Your money from Day 1</div>
                    <div style={{ fontSize: 12, color: "#2D5C45", lineHeight: 1.7 }}>
                      While the pool fills, your funds earn <strong>~{PARKING_OPTIONS.find(o => o.id === parkingPropertyId)?.yield || 7}% p.a.</strong> in your chosen token. Once target is reached, your stake converts to {p.name} automatically.
                    </div>
                  </div>

                  {/* Backed by */}
                  <div style={{ background: "#F8F5F0", border: "1px solid #EDE4DC", borderRadius: 14, padding: "16px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Backed by</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {["", "", ""].map(b => (
                        <span key={b} style={{ padding: "4px 10px", background: "#fff", border: "1px solid #EDE4DC", borderRadius: 6, fontSize: 11, color: "#444", fontWeight: 500 }}>{b}</span>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: "#999", marginTop: 10, lineHeight: 1.6 }}>
                      Acquired by <strong style={{ color: "#444" }}>White Soil Advisors LLP</strong> (LLPIN: AAT-7542) on behalf of all investors.
                    </div>
                  </div>
                </div>
              </div>

              {/* ── COMMIT BOX ── */}
              {isSubmitted ? (
                <div style={{ background: "linear-gradient(135deg, #F0F7F3, #E8F4EE)", border: "1px solid #C8E6D4", borderRadius: 16, padding: "28px", textAlign: "center" }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "#1B3A2D", marginBottom: 8 }}>Funds committed!</div>
                  <div style={{ fontSize: 13, color: "#2D5C45", lineHeight: 1.7 }}>
                    {submitData?.msg || `Your ₹${commitAmount.toLocaleString("en-IN")} is now earning rental income.`}
                  </div>
                  <a href="/my-community" style={{ display: "inline-block", marginTop: 16, padding: "10px 24px", background: "#1B3A2D", color: "#fff", borderRadius: 10, fontWeight: 700, fontSize: 13, textDecoration: "none" }}>
                    Track in My Community →
                  </a>
                </div>
              ) : (
                <div style={{ background: "#FAFAF8", border: "1.5px solid #EDE4DC", borderRadius: 16, padding: "20px" }}>
                  {/* Parking token selector */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 8 }}>Park funds in (earn while pooling):</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {PARKING_OPTIONS.map(opt => (
                        <button key={opt.id} onClick={() => setParkingPropertyId(opt.id)}
                          style={{ padding: "8px 14px", borderRadius: 10, border: `1.5px solid ${parkingPropertyId === opt.id ? "#1B3A2D" : "#E0DDD8"}`, background: parkingPropertyId === opt.id ? "#F0F7F3" : "#fff", color: parkingPropertyId === opt.id ? "#1B3A2D" : "#888", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                          <div>{opt.name}</div>
                          <div style={{ fontSize: 10, opacity: 0.7 }}>{opt.yield > 0 ? `${opt.yield}% p.a.` : "Appreciation"} · ₹{opt.tokenPrice}/token</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>Reserve your stake</div>
                    <div style={{ fontSize: 12, color: "#999" }}>Minimum ₹{p.minCommit}</div>
                  </div>

                  {/* Amount input */}
                  <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                    <div style={{ flex: 1, display: "flex", alignItems: "center", background: "#fff", border: "2px solid #1B3A2D", borderRadius: 12, padding: "10px 16px", gap: 8 }}>
                      <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#1B3A2D", fontWeight: 700 }}>₹</span>
                      <input type="text" inputMode="numeric"
                        value={commitAmount.toLocaleString("en-IN")}
                        onChange={e => { const n = Math.max(0, Math.min(parseInt(e.target.value.replace(/\D/g, "") || "0"), 10000000)); setCommitAmount(n); }}
                        style={{ flex: 1, border: "none", outline: "none", fontSize: 20, fontWeight: 700, color: "#111", background: "none", fontFamily: "'DM Sans', sans-serif" }} />
                    </div>
                    <button className="ec-commit-btn" onClick={() => handleCommit(p.id, p.name, p.parkingIn)} disabled={loading || commitAmount < 100}
                      style={{ padding: "12px 24px", background: commitAmount < 100 ? "#ccc" : "linear-gradient(135deg, #1B3A2D, #2D5C45)", color: "#fff", border: "none", borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: commitAmount < 100 ? "not-allowed" : "pointer", whiteSpace: "nowrap", fontFamily: "'DM Sans', sans-serif" }}>
                      {loading ? "..." : user ? "Commit Funds →" : "Sign up →"}
                    </button>
                  </div>

                  {/* Quick amounts */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {[500, 1000, 5000, 10000, 50000].map(v => (
                      <button key={v} className="ec-qa" onClick={() => setCommitAmount(v)}
                        style={{ padding: "6px 14px", borderRadius: 100, border: `1.5px solid ${commitAmount === v ? "#1B3A2D" : "#E0DDD8"}`, background: commitAmount === v ? "#F0F7F3" : "#fff", color: commitAmount === v ? "#1B3A2D" : "#888", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s" }}>
                        {v >= 1000 ? "₹" + (v / 1000) + "K" : "₹" + v}
                      </button>
                    ))}
                  </div>

                  {/* Live return preview */}
                  {commitAmount >= 100 && (
                    <div style={{ marginTop: 14, padding: "12px 16px", background: "#F0F7F3", borderRadius: 10, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                      <div style={{ fontSize: 12, color: "#2D5C45" }}>
                        While pooling: <strong>~₹{Math.round(commitAmount * ((PARKING_OPTIONS.find(o => o.id === parkingPropertyId)?.yield || 7) / 100) / 365)}/day</strong> from {PARKING_OPTIONS.find(o => o.id === parkingPropertyId)?.name || p.parkingIn}
                      </div>
                      <div style={{ fontSize: 12, color: "#1B3A2D", fontWeight: 600 }}>
                        After acquisition: ~₹{Math.round(commitAmount * (p.rentalYield / 100) / 12)}/mo from {p.name}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── LEGAL FOOTER ── */}
            <div style={{ padding: "14px 24px", borderTop: "1px solid #F0EDE8", background: "#FAFAF8" }}>
              <p style={{ margin: 0, fontSize: 10, color: "#bbb", lineHeight: 1.8 }}>
                ⚠️ Community property pooling is subject to reaching the target amount. If the target is not reached, all committed funds remain invested in existing EstateCoin tokens and can be withdrawn at any time. Rental yields are as per developer/leasing partner agreements and are not guaranteed by EstateCoin. Property acquisition will be executed by White Soil Advisors LLP (LLPIN AAT-7542) on behalf of investors. Investment involves market risk. This is not a solicitation to invest. Read our <a href="/terms" style={{ color: "#aaa" }}>Terms</a> and <a href="/risk-disclosure" style={{ color: "#aaa" }}>Risk Disclosure</a>.
              </p>
            </div>
          </div>
        );
      })}

      {/* ── SUGGEST A PROPERTY ── */}
      <div style={{ background: "linear-gradient(135deg, #1B3A2D, #264D38)", borderRadius: 20, padding: "32px", textAlign: "center", color: "white" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>💡</div>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Know a great property?</div>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", margin: "0 auto 20px", maxWidth: 400, lineHeight: 1.7 }}>
          Spotted a property in your city that you think EstateCoin should acquire? Our team reviews every suggestion.
        </p>
        <Link to="/suggest-property"
          style={{ display: "inline-block", padding: "12px 28px", background: "linear-gradient(135deg, #C9A84C, #E8C97A)", color: "#1B3A2D", borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: "none" }}>
          Suggest a Property →
        </Link>
      </div>
    </div>
  );
}
